import { useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { Field } from './Field.js';

/**
 * Checks a display name against `check_display_name` and returns a user-facing
 * error message, or null if it's acceptable (or on a network hiccup — the
 * submit-time check and DB trigger are the authoritative backstops). Shared by
 * the on-blur field feedback and the wizard's on-Next gate so both agree.
 */
export async function checkDisplayNameError(name: string): Promise<string | null> {
  const trimmed = name.trim();
  if (!trimmed) return null; // emptiness is the step's synchronous 'Required' check
  const { data: status, error } = await supabase.rpc('check_display_name', { name: trimmed });
  if (error) return null;
  if (status === 'not_allowed') return "That display name isn't allowed — choose a different one.";
  if (status === 'taken') return 'That display name is already taken — choose a different one.';
  return null;
}

/**
 * Display-name input with early (on-blur) validation. When the learner/guide
 * leaves the field, it surfaces a banned or taken name right here on the first
 * step. The wizard also re-checks on "Next" (see step.validateAsync) so a bad
 * name blocks progress even if the field was never blurred.
 *
 * The `error` prop is the wizard's error (sync 'Required' or the on-Next check)
 * and takes precedence over this field's own on-blur result.
 */
export function DisplayNameField({
  value,
  update,
  error,
}: {
  value: string;
  update: (patch: { displayName: string }) => void;
  error?: string;
}) {
  const [checkError, setCheckError] = useState<string | null>(null);

  return (
    <Field label="Display name" hint="Shown to others in the lobby." error={error ?? checkError ?? undefined}>
      <input
        className="input"
        maxLength={32}
        value={value}
        onChange={(e) => {
          setCheckError(null); // clear stale result while they edit; re-checks on next blur / Next
          update({ displayName: e.target.value });
        }}
        onBlur={async () => setCheckError(await checkDisplayNameError(value))}
      />
    </Field>
  );
}
